/**
 * Types and functions for allocators (short Alloc) that are used throughout the geom library including
 * tess and typed array based geometry like PolyLine2
 * @module
 */
export interface Alloc {
  allocFloat64(n: number): Float64Array;
  allocFloat32(n: number): Float32Array;
  allocUint32(n: number): Uint32Array;
}

/**
 * ArenaAlloc is a simple kind of allocator that has an offsetBytes field
 * pointing to the end of allocated memory and a reset method
 * that allows that offset to be zero'ed. It's useful if you
 * have a known amt of memory needed for the allocator lifetime.
 * It is nearly zero cost to make an allocation and rezero, but is
 * wasteful as you can't deallocate any part of memory except for
 * rezeroing.
 */
export class ArenaAlloc {
  private offsetBytes: number = 0;
  private readonly maxBytes: number;

  constructor(readonly buffer: ArrayBuffer | SharedArrayBuffer) {
    this.maxBytes = buffer.byteLength;
  }

  allocFloat32(n: number): Float32Array {
    const offset = this.offsetBytes;
    this.offsetBytes += n * 4;
    if (this.offsetBytes > this.maxBytes) {
      throw new Error("Out of memory");
    }
    return new Float32Array(this.buffer, offset, n);
  }

  allocFloat64(n: number): Float64Array {
    const offset = this.offsetBytes;
    this.offsetBytes += n * 8;
    if (this.offsetBytes > this.maxBytes) {
      throw new Error("Out of memory");
    }
    return new Float64Array(this.buffer, offset, n);
  }

  allocUint32(n: number): Uint32Array {
    const offset = this.offsetBytes;
    this.offsetBytes += n * 4;
    if (this.offsetBytes > this.maxBytes) {
      throw new Error("Out of memory");
    }
    return new Uint32Array(this.buffer, offset, n);
  }

  reset() {
    this.offsetBytes = 0;
  }
}

/**
 * AutoGrowArenaAlloc works in a similar way to ArenaAlloc, but it adds
 * new chunks of memory when the last added chunk is full. We can define
 * the chunk size and the maximum number on chunks. But default no maximum
 * chunk count is defined.
 */
export class AutoGrowArenaAlloc {
  private offsetBytes: number = 0;
  private currBuffer: number = 0;
  private readonly buffers: ArrayBuffer[] = [];

  constructor(
    private readonly name: string,
    private readonly maxBytesPerBuffer: number,
    private readonly maxBufferCount?: number,
  ) {
    this.buffers.push(new ArrayBuffer(this.maxBytesPerBuffer));
  }

  private autoGrow() {
    this.currBuffer++;

    if (this.currBuffer >= this.buffers.length) {
      if (this.maxBufferCount !== undefined && this.buffers.length >= this.maxBufferCount) {
        throw new Error(`Out of memory for "${this.name}" arena`);
      }

      console.warn(`Increasing "${this.name}" arena size`);

      this.buffers.push(new ArrayBuffer(this.maxBytesPerBuffer));
    }

    this.offsetBytes = 0;
  }

  private alloc(size: number): number {
    if (size > this.maxBytesPerBuffer) {
      throw new Error(`Out of memory for "${this.name}" arena`);
    }

    let offset = this.offsetBytes;
    this.offsetBytes += size;

    if (this.offsetBytes > this.maxBytesPerBuffer) {
      this.autoGrow();
      offset = 0;
    }

    return offset;
  }

  allocFloat32(n: number): Float32Array {
    const offset = this.alloc(n * 4);
    return new Float32Array(this.buffers[this.currBuffer], offset, n);
  }

  allocFloat64(n: number): Float64Array {
    const offset = this.alloc(n * 8);
    return new Float64Array(this.buffers[this.currBuffer], offset, n);
  }

  allocUint32(n: number): Uint32Array {
    const offset = this.alloc(n * 4);
    return new Uint32Array(this.buffers[this.currBuffer], offset, n);
  }

  getTotalSize(): number {
    return this.buffers.length * this.maxBytesPerBuffer;
  }

  getUsedSize(): number {
    return this.currBuffer * this.maxBytesPerBuffer + this.offsetBytes;
  }

  reset() {
    this.currBuffer = 0;
    this.offsetBytes = 0;
  }
}

/**
 * HeapAlloc simply uses the GC'ed heap.
 */
export class HeapAlloc {
  allocFloat32(n: number): Float32Array {
    return new Float32Array(n);
  }

  allocFloat64(n: number): Float64Array {
    return new Float64Array(n);
  }

  allocUint32(n: number): Uint32Array {
    return new Uint32Array(n);
  }
}
